// src/screens/s2/CTPosition.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 7 — CT Position.
// Historic: outstanding CT + VAT + PAYE; clearance plan bar chart.
// Forward:  projected annual profit → effective CT rate with marginal-relief
//           context → reserve accumulation line chart against the projected
//           bill with the CT due date (9 months + 1 day after FY end).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroData } from '../../hooks/useXeroData';
import { useXeroBalance } from '../../hooks/useXeroBalance';
import { effectiveCTRate } from '../../taxConfig';
import { formatCurrency, formatMonths, formatPercent } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// UK CT is due 9 months and 1 day after the end of the accounting period.
// Default FY end 31 March; can be overridden on the client data eventually.
function ctDueDate(today: Date, fyEndMonth: number = 3, fyEndDay: number = 31): Date {
  const y = today.getUTCFullYear();
  const thisYearFyEnd = new Date(Date.UTC(y, fyEndMonth - 1, fyEndDay));
  // If today is past this year's FY end, the next CT falls due 9mo+1d later.
  const priorFyEnd = today > thisYearFyEnd ? thisYearFyEnd : new Date(Date.UTC(y - 1, fyEndMonth - 1, fyEndDay));
  return new Date(Date.UTC(priorFyEnd.getUTCFullYear(), priorFyEnd.getUTCMonth() + 9, priorFyEnd.getUTCDate() + 1));
}

const monthsBetween = (from: Date, to: Date) => {
  const ms = to.getTime() - from.getTime();
  return ms / (1000 * 60 * 60 * 24 * 30.4375);
};

export default function CTPosition({ data, onUpdate }: Props) {
  const xero = useXeroData(data.xeroTenantId);
  const balance = useXeroBalance(data.xeroTenantId);

  if (xero.loading || balance.loading) {
    return <div className="p-6 max-w-6xl mx-auto text-sm text-gray-500">Loading CT position…</div>;
  }
  if (!xero.mapped || !xero.summary || !balance.data) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">CT Position</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          This screen needs live P&amp;L + balance sheet. {xero.error || balance.error || 'Connect and map Xero first.'}
        </div>
      </div>
    );
  }

  const s = xero.summary!;
  const b = balance.data!;

  // ── HISTORIC ────────────────────────────────────────────────────────────
  const historicCT = Math.max(0, b.ctLiability);
  const historicVAT = Math.max(0, b.vatLiability);
  const historicPAYE = Math.max(0, b.payeLiability);
  const historicTotal = historicCT + historicVAT + historicPAYE;
  const clearanceMonths = data.s2HistoricCtLiabilityClearanceMonths ?? 12;
  const monthlyClearance = clearanceMonths > 0 ? historicTotal / clearanceMonths : 0;

  // Build the reducing-liability bar chart — one bar per month, diminishing.
  const clearanceSeries: Array<{ month: string; balance: number }> = [];
  const today = new Date();
  for (let m = 0; m <= clearanceMonths; m++) {
    const d = new Date(today.getFullYear(), today.getMonth() + m, 1);
    const balanceRemaining = Math.max(0, historicTotal - monthlyClearance * m);
    clearanceSeries.push({
      month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      balance: Math.round(balanceRemaining),
    });
  }

  // ── FORWARD ─────────────────────────────────────────────────────────────
  const projectedProfit = s.netProfit;    // pre-CT profit from Xero summary
  const ctRate = effectiveCTRate(projectedProfit);
  const projectedCTBill = projectedProfit * ctRate;
  const monthlyReserve = data.s2CtReserveMonthly ?? projectedCTBill / 12;

  // Time horizon runs from today through the CT due date + 1 month buffer so
  // the crossover point between reserve and bill is always in frame.
  const due = ctDueDate(today);
  const horizonMonths = Math.max(12, Math.ceil(monthsBetween(today, due)) + 2);
  const forwardSeries: Array<{ month: string; reserve: number; bill: number }> = [];
  for (let m = 0; m <= horizonMonths; m++) {
    const d = new Date(today.getFullYear(), today.getMonth() + m, 1);
    forwardSeries.push({
      month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      reserve: Math.round(monthlyReserve * m),
      bill: Math.round(projectedCTBill),
    });
  }

  // When reserve meets or exceeds the bill — that's the coverage month.
  const coverageMonth = monthlyReserve > 0 ? Math.ceil(projectedCTBill / monthlyReserve) : Infinity;
  const dueMonthLabel = `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, '0')}`;
  const dueMonthsAway = Math.ceil(monthsBetween(today, due));
  const monthsBeforeDue = dueMonthsAway - coverageMonth;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          CT Position
          {xero.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {xero.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          What you owe HMRC today and what you'll owe when this year's return
          falls due.
        </p>
      </div>

      {/* ── HISTORIC SECTION ─────────────────────────────────────────────── */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
        Historic liabilities
      </h3>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">CT outstanding</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(historicCT)}</div>
        </div>
        <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">VAT outstanding</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(historicVAT)}</div>
        </div>
        <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">PAYE outstanding</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(historicPAYE)}</div>
        </div>
        <div className="rounded-2xl border-2 p-4" style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}>
          <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1">Total</div>
          <div className="text-2xl font-bold text-red-700">{formatCurrency(historicTotal)}</div>
        </div>
      </div>

      {/* Clearance input + chart */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Clearance plan</div>
            <div className="flex items-baseline gap-2">
              <input
                type="number"
                min={1}
                value={clearanceMonths}
                onChange={(e) => onUpdate({ s2HistoricCtLiabilityClearanceMonths: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                className="text-2xl font-bold text-ma-navy bg-transparent border border-gray-300 rounded px-2 py-1 w-20"
              />
              <span className="text-sm text-gray-500">months to clear</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Monthly payment</div>
            <div className="text-xl font-bold text-ma-navy">{formatCurrency(monthlyClearance)}/mo</div>
          </div>
        </div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={clearanceSeries} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} stroke="#9CA3AF" interval={Math.max(0, Math.floor(clearanceMonths / 10))} />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <Bar dataKey="balance" fill="#DC2626" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── FORWARD SECTION ──────────────────────────────────────────────── */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
        Forward — this year's CT bill
      </h3>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Projected annual profit</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(projectedProfit)}</div>
          <p className="text-xs text-gray-500 mt-1">From Xero P&amp;L (pre-CT)</p>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Effective CT rate</div>
          <div className="text-2xl font-bold text-ma-navy">{formatPercent(ctRate * 100)}</div>
          <p className="text-xs text-gray-500 mt-1">
            {projectedProfit <= 50000
              ? 'Small profits rate (≤£50k)'
              : projectedProfit >= 250000
                ? 'Main rate (≥£250k)'
                : `Marginal relief — tapered between 19% and 25%`}
          </p>
        </div>
        <div className="rounded-2xl border-2 p-4" style={{ borderColor: '#C9A84C', backgroundColor: '#FFF9ED' }}>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#8B7A3E' }}>
            Projected CT bill
          </div>
          <div className="text-2xl font-bold" style={{ color: '#C9A84C' }}>{formatCurrency(projectedCTBill)}</div>
          <p className="text-xs mt-1" style={{ color: '#8B7A3E' }}>Due {dueMonthLabel}</p>
        </div>
      </div>

      {/* Reserve input + line chart */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Monthly reserve SO</div>
            <div className="flex items-baseline gap-2">
              <span className="text-gray-400">£</span>
              <input
                type="number"
                min={0}
                value={Math.round(monthlyReserve)}
                onChange={(e) => onUpdate({ s2CtReserveMonthly: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                className="text-2xl font-bold text-ma-navy bg-transparent border border-gray-300 rounded px-2 py-1 w-32"
              />
              <span className="text-xs text-gray-500">/mo into CT reserve account</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Rule of thumb</div>
            <div className="text-lg font-semibold text-gray-700">{formatCurrency(projectedCTBill / 12)}/mo</div>
            <div className="text-xs text-gray-400">= CT bill ÷ 12</div>
          </div>
        </div>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={forwardSeries} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} stroke="#9CA3AF" interval={Math.max(0, Math.floor(horizonMonths / 8))} />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <ReferenceLine x={dueMonthLabel} stroke="#DC2626" strokeDasharray="4 2" label={{ value: 'CT due', fontSize: 10, fill: '#DC2626', position: 'insideTopRight' }} />
              <Line type="monotone" dataKey="bill" stroke="#C9A84C" strokeWidth={2} strokeDasharray="3 3" dot={false} />
              <Line type="monotone" dataKey="reserve" stroke="#10B981" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
            <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide mb-1">Coverage</div>
            <div className="text-sm text-emerald-800">
              {isFinite(coverageMonth) && monthlyReserve > 0 ? (
                monthsBeforeDue >= 0
                  ? <>At this rate you will cover your CT bill <strong>{formatMonths(monthsBeforeDue)} month{monthsBeforeDue === 1 ? '' : 's'}</strong> before it is due.</>
                  : <>At this rate you will <strong>fall short by {formatMonths(Math.abs(monthsBeforeDue))} month{Math.abs(monthsBeforeDue) === 1 ? '' : 's'}</strong>. Increase the monthly reserve.</>
              ) : 'Set a monthly reserve to see coverage.'}
            </div>
          </div>
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
            <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Dates</div>
            <div className="text-sm text-gray-700">
              Bill due <strong>{dueMonthLabel}</strong> ({formatMonths(dueMonthsAway)} months away). Coverage reached at month <strong>{isFinite(coverageMonth) ? coverageMonth : '—'}</strong>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
